import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { memo, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import ToastGroup from './ToastGroup';
import { getOverrides, useClasses } from '../../../utils/overrides';
import { createUseStyles } from '../../../utils/styles';
import { useToastContainer, useToastAutoClose } from './hooks';
import { POSITION } from './constants';
import { CLEAR_TOAST, publish } from '../../../utils/eventBuser';
import styles from './styles';
import { TransitionGroup } from './transitions';
var useStyles = createUseStyles(styles, 'ToastContainer');
var DEFAULT_CLOSE_TIME = 4000;
var ToastContainer = /*#__PURE__*/memo(function (_ref) {
  var classesProp = _ref.classes,
    classNameProp = _ref.className,
    overridesProp = _ref.overrides,
    _ref$autoClose = _ref.autoClose,
    autoClose = _ref$autoClose === void 0 ? false : _ref$autoClose,
    useDefaultCloseButton = _ref.useDefaultCloseButton,
    closeOnClick = _ref.closeOnClick,
    position = _ref.position,
    transition = _ref.transition,
    newestOnTop = _ref.newestOnTop,
    preComponent = _ref.preComponent,
    postComponent = _ref.postComponent,
    containerId = _ref.containerId;
  var _useToastContainer = useToastContainer({
      position: position,
      transition: transition,
      autoClose: autoClose,
      useDefaultCloseButton: useDefaultCloseButton,
      closeOnClick: closeOnClick,
      newestOnTop: newestOnTop,
      containerId: containerId
    }),
    toasts = _useToastContainer.toasts,
    setToasts = _useToastContainer.setToasts,
    clearDeletedToast = _useToastContainer.clearDeletedToast;
  var classes = useClasses(useStyles, classesProp);
  var override = getOverrides(overridesProp, ToastContainer.overrides);
  var rootClassName = classnames(classes.root, {}, classNameProp);
  useToastAutoClose({
    toasts: toasts,
    setToasts: setToasts,
    autoClose: autoClose,
    autoCloseTime: autoClose && typeof autoClose !== 'boolean' && !isNaN(autoClose) ? autoClose : DEFAULT_CLOSE_TIME
  });
  var removeToast = useCallback(function (id) {
    publish(CLEAR_TOAST, {
      id: id
    });
  }, []);
  var rootProps = useMemo(function () {
    var props = {};
    props.id = containerId || 'hoi-poi-ui-toast-container';
    return props;
  }, [containerId]);
  return /*#__PURE__*/React.createElement("div", _extends({
    className: rootClassName
  }, rootProps, override.root), /*#__PURE__*/React.createElement(TransitionGroup, {
    className: classes.TransitionGroup
  }, Object.entries(POSITION).map(function (_ref2) {
    var _ref3 = _slicedToArray(_ref2, 2),
      key = _ref3[0],
      value = _ref3[1];
    var finalPreComponent = preComponent === null || preComponent === void 0 ? void 0 : preComponent[key];
    var finalPostComponent = postComponent === null || postComponent === void 0 ? void 0 : postComponent[key];
    return /*#__PURE__*/React.createElement(ToastGroup, {
      key: key,
      position: key,
      toasts: toasts[value] || [],
      removeToast: removeToast,
      clearDeletedToast: clearDeletedToast,
      preComponent: finalPreComponent,
      postComponent: finalPostComponent,
      override: override
    });
  })));
});
ToastContainer.overrides = ['root', 'ToastGroup', 'Toast', 'ToastWrapper'];
ToastContainer.defaultProps = {
  className: '',
  overrides: {},
  position: 'topRight',
  autoClose: 4000,
  newestOnTop: true,
  closeOnClick: false
};
process.env.NODE_ENV !== "production" ? ToastContainer.propTypes = {
  className: PropTypes.string,
  overrides: PropTypes.object,
  content: PropTypes.element,
  closeButton: PropTypes.element,
  closeButtonClassName: PropTypes.string,
  closeOnClick: PropTypes.bool,
  newestOnTop: PropTypes.bool,
  position: PropTypes.oneOf(['topLeft', 'topCenter', 'topRight', 'bottomLeft', 'bottomCenter', 'bottomRight']),
  preComponent: PropTypes.shape({
    topLeft: PropTypes.element,
    topCenter: PropTypes.element,
    topRight: PropTypes.element,
    bottomLeft: PropTypes.element,
    bottomCenter: PropTypes.element,
    bottomRight: PropTypes.element
  }),
  postComponent: PropTypes.shape({
    topLeft: PropTypes.element,
    topCenter: PropTypes.element,
    topRight: PropTypes.element,
    bottomLeft: PropTypes.element,
    bottomCenter: PropTypes.element,
    bottomRight: PropTypes.element
  }),
  autoClose: PropTypes.oneOfType([PropTypes.number, PropTypes.bool])
} : void 0;
export default ToastContainer;